import type { QueryClient } from '@tanstack/react-query';
import { binaryTreeAPI } from '../api/binary-tree';

/** Depth-3 binary window is small; keep payload light. */
export const GLOBAL_TABLE_PAGE_SIZE = 32;
/** Visible BFS layers relative to the current root (1.1 → 2.x → 3.x). */
export const GLOBAL_TABLE_VISIBLE_DEPTH = 3;
export const GLOBAL_TABLE_QUERY_KEY = ['binary-tree', 'global-table'] as const;
/** Longer stale window — table is read-heavy; avoid refetch storms while drilling. */
export const GLOBAL_TABLE_STALE_MS = 90_000;

export function globalTableQueryKey(
  rootFilter = '',
  rootUserId: number | null = null,
  page = 1,
  maxDepth: number | null = GLOBAL_TABLE_VISIBLE_DEPTH,
): readonly unknown[] {
  return [...GLOBAL_TABLE_QUERY_KEY, rootFilter, rootUserId, page, maxDepth];
}

/** Warm global players table while user navigates the app. */
export function prefetchGlobalTable(qc: QueryClient, rootFilter = '', rootUserId: number | null = null): void {
  void qc.prefetchQuery({
    queryKey: globalTableQueryKey(rootFilter, rootUserId, 1),
    queryFn: () =>
      binaryTreeAPI.getGlobalTable({
        ...(rootUserId != null && rootUserId > 0
          ? { root_user_id: rootUserId }
          : rootFilter
            ? { root_player_number: rootFilter }
            : {}),
        page: 1,
        limit: GLOBAL_TABLE_PAGE_SIZE,
        max_depth: GLOBAL_TABLE_VISIBLE_DEPTH,
      }),
    staleTime: GLOBAL_TABLE_STALE_MS,
  });
}

/**
 * Optional drill prefetch — disabled by default (hover storms made the page hang:
 * each call is a 1–3s global-table round-trip).
 * Keep as no-op so call sites stay safe; click navigation loads on demand.
 */
export function prefetchGlobalTableDrill(
  _qc: QueryClient,
  _rootFilter: string,
  _rootUserId: number,
): void {
  // intentionally no-op
}
